import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Select,
  Button,
  Spin,
  Typography,
  Badge,
  Space,
  Alert,
  Statistic,
  Progress,
  Table,
  Tag,
  Empty,
  message,
} from "antd";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import { motion } from "framer-motion";
import { useHistory } from "react-router-dom";
import moment from "moment-timezone";
import styled from "styled-components";
import { useQuery } from "react-query";
import {
  MailOutlined,
  LineChartOutlined,
  CalendarOutlined,
  RiseOutlined,
  ThunderboltOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  SendOutlined,
} from "@ant-design/icons";

// API
import {
  getPulseReportsDashboardStats,
  getPulseReports,
  getPulseReportProgress,
  pauseRunningPulseReport,
  stopPulseReport,
} from "@api/pulse-reports/pulseReport";

// Components
import { PulseReportProgressCard } from "@components";

const { Title, Text } = Typography;

// Styled Components
const StyledCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  height: 100%;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .ant-card-head-title {
    font-weight: 600;
  }
`;

// Motion Components
const MetricCard = styled(motion.div)`
  padding: 24px;
  border-radius: 12px;
  background: ${(props) => props.background || "#fff"};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  height: 100%;

  .metric-title {
    color: rgba(0, 0, 0, 0.45);
    font-size: 14px;
    margin-bottom: 8px;
  }

  .metric-value {
    font-size: 32px;
    font-weight: 600;
    color: ${(props) => props.valueColor || "rgba(0, 0, 0, 0.85)"};
    margin-bottom: 8px;
  }

  .metric-trend {
    font-size: 14px;
  }
`;

const ChartContainer = styled.div`
  height: 400px;
  width: 100%;
`;

const PulseReportsAnalyticsDashboard = () => {
  const history = useHistory();
  const [timeRange, setTimeRange] = useState("30d");
  const [sendingProgress, setSendingProgress] = useState({});
  const [actionLoading, setActionLoading] = useState({});

  // Fetch dashboard stats
  const {
    data: statsResponse,
    isLoading: statsLoading,
    error: statsError,
  } = useQuery(
    ["pulseReportsDashboardStats", timeRange],
    async () => {
      const response = await getPulseReportsDashboardStats(timeRange);
      return response.data;
    },
    {
      refetchInterval: 30000,
      staleTime: 20000,
      keepPreviousData: true,
    }
  );

  // Fetch pulse reports list
  const { data: reportsResponse, isLoading: reportsLoading } = useQuery(
    ["pulseReportsList"],
    async () => {
      const response = await getPulseReports(1, 100);
      return response.data;
    },
    {
      staleTime: 60000,
    }
  );

  const stats = statsResponse?.data;
  const reports = reportsResponse?.data || [];

  // Get sending reports
  const sendingReports = reports.filter((r) => r.status === "sending");

  // Fetch progress for sending reports
  useEffect(() => {
    if (sendingReports.length === 0) return;

    const fetchProgress = async () => {
      for (const report of sendingReports) {
        try {
          const progressRes = await getPulseReportProgress(report._id);
          if (progressRes.data) {
            setSendingProgress((prev) => ({
              ...prev,
              [report._id]: progressRes.data,
            }));
          }
        } catch (err) {
          console.error("Error fetching progress for", report._id, err);
        }
      }
    };

    fetchProgress();
    // Set up interval to refresh progress every 10 seconds
    const intervalId = setInterval(fetchProgress, 10000);

    return () => clearInterval(intervalId);
  }, [sendingReports.length]);

  // Handle pause running report
  const handlePauseRunning = async (id) => {
    setActionLoading((prev) => ({ ...prev, [id]: "pause" }));
    try {
      await pauseRunningPulseReport(id);
      message.success("Pulse report paused. You can resume from checkpoint later.");
    } catch (error) {
      console.error("Error pausing pulse report", error);
      message.error("Failed to pause pulse report");
    } finally {
      setActionLoading((prev) => ({ ...prev, [id]: null }));
    }
  };

  // Handle stop report
  const handleStop = async (id) => {
    setActionLoading((prev) => ({ ...prev, [id]: "stop" }));
    try {
      const result = await stopPulseReport(id);
      message.success(
        `Pulse report stopped. Sent: ${result.data?.finalStats?.sent || 0}, Failed: ${result.data?.finalStats?.failed || 0}`
      );
    } catch (error) {
      console.error("Error stopping pulse report", error);
      message.error("Failed to stop pulse report");
    } finally {
      setActionLoading((prev) => ({ ...prev, [id]: null }));
    }
  };

  // Calculate counts from reports
  const reportCounts = {
    total: reports.length,
    active: reports.filter((r) => r.status === "active").length,
    paused: reports.filter((r) => r.status === "paused").length,
    draft: reports.filter((r) => r.status === "draft").length,
    sending: sendingReports.length,
    failed: reports.filter((r) => r.status === "failed").length,
  };

  if (statsError) {
    return (
      <Alert
        message="Error Loading Dashboard"
        description={statsError.message}
        type="error"
        showIcon
        style={{ margin: 24 }}
      />
    );
  }

  // Show initial loading state with animated loader
  if (statsLoading && !stats) {
    return (
      <div style={{ padding: "24px" }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "70vh",
            padding: "64px",
          }}
        >
          {/* Animated Icons */}
          <motion.div
            style={{
              display: "flex",
              gap: "24px",
              marginBottom: "48px",
            }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            {[
              { icon: <FileTextOutlined />, color: "#722ED1", delay: 0 },
              { icon: <SendOutlined />, color: "#1890FF", delay: 0.1 },
              { icon: <LineChartOutlined />, color: "#52C41A", delay: 0.2 },
              { icon: <TeamOutlined />, color: "#FAAD14", delay: 0.3 },
              { icon: <RiseOutlined />, color: "#FF4D4F", delay: 0.4 },
            ].map((item, index) => (
              <motion.div
                key={index}
                style={{
                  width: "56px",
                  height: "56px",
                  borderRadius: "16px",
                  background: `linear-gradient(135deg, ${item.color}15 0%, ${item.color}30 100%)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "28px",
                  color: item.color,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                }}
                animate={{
                  y: [0, -12, 0],
                  scale: [1, 1.1, 1],
                }}
                transition={{
                  duration: 2,
                  delay: item.delay,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                {item.icon}
              </motion.div>
            ))}
          </motion.div>

          {/* Spinner with Gradient Ring */}
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            style={{ marginBottom: "32px" }}
          >
            <div style={{ position: "relative", width: "80px", height: "80px" }}>
              <Spin
                size="large"
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              />
              <motion.div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: "50%",
                  border: "3px solid transparent",
                  borderTopColor: "#722ed1",
                  borderRightColor: "#1890ff",
                  opacity: 0.3,
                }}
                animate={{ rotate: 360 }}
                transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
              />
            </div>
          </motion.div>

          {/* Loading Text */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            style={{ textAlign: "center" }}
          >
            <Title
              level={3}
              style={{
                margin: 0,
                marginBottom: "12px",
                background: "linear-gradient(135deg, #722ed1 0%, #1890ff 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                fontWeight: 600,
              }}
            >
              Loading Pulse Reports Analytics
            </Title>
            <motion.div
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            >
              <Text
                type="secondary"
                style={{
                  fontSize: "16px",
                  display: "block",
                }}
              >
                Gathering insights from all your pulse reports...
              </Text>
            </motion.div>
          </motion.div>

          {/* Progress Indicators */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.8 }}
            style={{
              marginTop: "48px",
              display: "flex",
              gap: "32px",
              alignItems: "center",
            }}
          >
            {[
              { icon: <FileTextOutlined />, label: "Loading Reports" },
              { icon: <LineChartOutlined />, label: "Processing Metrics" },
              { icon: <RiseOutlined />, label: "Calculating Rates" },
            ].map((item, index) => (
              <motion.div
                key={index}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                }}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  duration: 0.4,
                  delay: 1 + index * 0.2,
                  type: "spring",
                  stiffness: 200,
                }}
              >
                <motion.div
                  animate={{
                    rotate: [0, 360],
                    scale: [1, 1.2, 1],
                  }}
                  transition={{
                    duration: 3,
                    delay: index * 0.3,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  style={{
                    fontSize: "24px",
                    color: "#722ed1",
                    width: "48px",
                    height: "48px",
                    borderRadius: "12px",
                    background:
                      "linear-gradient(135deg, #722ed115 0%, #722ed130 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {item.icon}
                </motion.div>
                <Text
                  type="secondary"
                  style={{
                    fontSize: "12px",
                    fontWeight: 500,
                  }}
                >
                  {item.label}
                </Text>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    );
  }

  // Prepare chart data
  const prepareTimeSeriesData = () => {
    if (!stats?.dailyStats) return [];
    return stats.dailyStats.map((stat) => ({
      date: moment(stat.date).format("MMM DD"),
      Sent: stat.sent || 0,
      Delivered: stat.delivered || 0,
      Opened: stat.opened || 0,
      Clicked: stat.clicked || 0,
    }));
  };

  // Report status columns
  const reportColumns = [
    {
      title: "Report Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <Button
          type="link"
          onClick={() => history.push(`/super-admin/pulse-reports/${record._id}/stats`)}
        >
          {text}
        </Button>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const statusConfig = {
          active: { color: "green", icon: <PlayCircleOutlined /> },
          paused: { color: "orange", icon: <PauseCircleOutlined /> },
          sending: { color: "blue", icon: <SendOutlined /> },
          draft: { color: "default", icon: <FileTextOutlined /> },
          archived: { color: "default", icon: <ClockCircleOutlined /> },
          failed: { color: "red", icon: <ClockCircleOutlined /> },
        };
        const config = statusConfig[status] || statusConfig.draft;
        return (
          <Tag color={config.color} icon={config.icon}>
            {status?.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: "Total Sent",
      dataIndex: ["statistics", "totalSent"],
      key: "totalSent",
      render: (val) => val?.toLocaleString() || 0,
    },
    {
      title: "Open Rate",
      dataIndex: ["statistics", "openRate"],
      key: "openRate",
      render: (val) => `${Number(val || 0).toFixed(1)}%`,
    },
    {
      title: "Click Rate",
      dataIndex: ["statistics", "clickRate"],
      key: "clickRate",
      render: (val) => `${Number(val || 0).toFixed(1)}%`,
    },
    {
      title: "Last Run",
      dataIndex: ["statistics", "lastRunAt"],
      key: "lastRunAt",
      render: (val) => val ? moment(val).fromNow() : "Never",
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      {/* Header Section */}
      <div style={{ marginBottom: "24px" }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={2} style={{ margin: 0 }}>
              <FileTextOutlined style={{ marginRight: "12px", color: "#722ed1" }} />
              Pulse Reports Analytics
            </Title>
          </Col>
          <Col>
            <Space>
              <Select
                value={timeRange}
                onChange={setTimeRange}
                style={{ minWidth: 150 }}
                suffixIcon={<CalendarOutlined />}
              >
                <Select.Option value="7d">Last 7 Days</Select.Option>
                <Select.Option value="30d">Last 30 Days</Select.Option>
                <Select.Option value="90d">Last 90 Days</Select.Option>
              </Select>
              <Button
                type="default"
                icon={<FileTextOutlined />}
                onClick={() => history.push("/super-admin/pulse-reports/html-templates")}
              >
                HTML Templates
              </Button>
              <Button
                type="primary"
                icon={<ThunderboltOutlined />}
                onClick={() => history.push("/super-admin/pulse-reports/list")}
              >
                View All Report Campaigns
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Report Status Cards */}
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false}>
            <Statistic
              title="Total Reports"
              value={reportCounts.total}
              prefix={<FileTextOutlined style={{ color: "#722ed1" }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false}>
            <Statistic
              title="Active"
              value={reportCounts.active}
              prefix={<PlayCircleOutlined style={{ color: "#52c41a" }} />}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false}>
            <Statistic
              title="Paused"
              value={reportCounts.paused}
              prefix={<PauseCircleOutlined style={{ color: "#fa8c16" }} />}
              valueStyle={{ color: "#fa8c16" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false}>
            <Statistic
              title="Draft"
              value={reportCounts.draft}
              prefix={<FileTextOutlined style={{ color: "#8c8c8c" }} />}
            />
          </Card>
        </Col>
      </Row>

      {/* Sending Reports Progress Cards */}
      {sendingReports.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          style={{ marginBottom: 24 }}
        >
          <Title level={4} style={{ marginBottom: 16 }}>
            <SendOutlined style={{ marginRight: 8, color: "#1890ff" }} />
            Reports Currently Sending ({sendingReports.length})
          </Title>
          <Row gutter={[24, 24]}>
            {sendingReports.map((report) => (
              <Col xs={24} sm={24} md={12} lg={8} key={report._id}>
                <PulseReportProgressCard
                  pulseReport={report}
                  progress={sendingProgress[report._id]}
                  onPause={() => handlePauseRunning(report._id)}
                  onStop={() => handleStop(report._id)}
                  pauseLoading={actionLoading[report._id] === "pause"}
                  stopLoading={actionLoading[report._id] === "stop"}
                />
              </Col>
            ))}
          </Row>
        </motion.div>
      )}

      {/* Key Metrics */}
      {stats && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} md={6}>
              <MetricCard
                background="linear-gradient(135deg, #722ED1 0%, #531DAB 100%)"
                valueColor="#fff"
                animate={{ y: [50, 0], opacity: [0, 1] }}
                transition={{ duration: 0.5 }}
              >
                <Text
                  className="metric-title"
                  style={{ color: "rgba(255,255,255,0.85)" }}
                >
                  Total Emails Sent
                </Text>
                <div className="metric-value" style={{ color: "#fff" }}>
                  {(stats?.overview?.totalSent || 0).toLocaleString()}
                </div>
                <div
                  className="metric-trend"
                  style={{ color: "rgba(255,255,255,0.85)" }}
                >
                  <SendOutlined /> From all reports
                </div>
              </MetricCard>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <MetricCard
                background="linear-gradient(135deg, #52C41A 0%, #389E0D 100%)"
                valueColor="#fff"
                animate={{ y: [50, 0], opacity: [0, 1] }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <Text
                  className="metric-title"
                  style={{ color: "rgba(255,255,255,0.85)" }}
                >
                  Open Rate
                </Text>
                <div className="metric-value" style={{ color: "#fff" }}>
                  {Number(stats?.overview?.openRate || 0).toFixed(1)}%
                </div>
                <div
                  className="metric-trend"
                  style={{ color: "rgba(255,255,255,0.85)" }}
                >
                  <Badge
                    status={Number(stats?.overview?.openRate || 0) > 21.5 ? "success" : "warning"}
                    text={
                      <Text style={{ color: "#fff" }}>
                        {Number(stats?.overview?.openRate || 0) > 21.5 ? "Above" : "Below"} Industry Avg
                      </Text>
                    }
                  />
                </div>
              </MetricCard>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <MetricCard
                background="linear-gradient(135deg, #FAAD14 0%, #D48806 100%)"
                valueColor="#fff"
                animate={{ y: [50, 0], opacity: [0, 1] }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Text
                  className="metric-title"
                  style={{ color: "rgba(255,255,255,0.85)" }}
                >
                  Click Rate
                </Text>
                <div className="metric-value" style={{ color: "#fff" }}>
                  {Number(stats?.overview?.clickRate || 0).toFixed(1)}%
                </div>
                <div
                  className="metric-trend"
                  style={{ color: "rgba(255,255,255,0.85)" }}
                >
                  <Badge
                    status={Number(stats?.overview?.clickRate || 0) > 2.3 ? "success" : "warning"}
                    text={
                      <span style={{ color: "#fff" }}>Industry Avg: 2.3%</span>
                    }
                  />
                </div>
              </MetricCard>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <MetricCard
                background="linear-gradient(135deg, #FF4D4F 0%, #CF1322 100%)"
                valueColor="#fff"
                animate={{ y: [50, 0], opacity: [0, 1] }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <Text
                  className="metric-title"
                  style={{ color: "rgba(255,255,255,0.85)" }}
                >
                  Bounce Rate
                </Text>
                <div className="metric-value" style={{ color: "#fff" }}>
                  {Number(stats?.overview?.bounceRate || 0).toFixed(1)}%
                </div>
                <div
                  className="metric-trend"
                  style={{ color: "rgba(255,255,255,0.85)" }}
                >
                  <Badge
                    status={Number(stats?.overview?.bounceRate || 0) < 0.7 ? "success" : "warning"}
                    text={
                      <span style={{ color: "#fff" }}>Industry Avg: 0.7%</span>
                    }
                  />
                </div>
              </MetricCard>
            </Col>
          </Row>

          {/* Charts */}
          <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
            <Col xs={24} lg={16}>
              <StyledCard title="Engagement Over Time">
                <ChartContainer>
                  {prepareTimeSeriesData().length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={prepareTimeSeriesData()}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Area
                          type="monotone"
                          dataKey="Sent"
                          stackId="1"
                          stroke="#722ED1"
                          fill="#722ED1"
                          fillOpacity={0.3}
                        />
                        <Area
                          type="monotone"
                          dataKey="Opened"
                          stackId="2"
                          stroke="#52C41A"
                          fill="#52C41A"
                          fillOpacity={0.3}
                        />
                        <Area
                          type="monotone"
                          dataKey="Clicked"
                          stackId="3"
                          stroke="#FAAD14"
                          fill="#FAAD14"
                          fillOpacity={0.3}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <Empty description="No data available for the selected period" />
                  )}
                </ChartContainer>
              </StyledCard>
            </Col>

            <Col xs={24} lg={8}>
              <StyledCard title="Delivery Performance">
                <div
                  style={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "20px",
                  }}
                >
                  <Progress
                    type="dashboard"
                    percent={Number(Number(stats?.overview?.openRate || 0).toFixed(1))}
                    strokeColor={{
                      "0%": "#722ed1",
                      "100%": "#1890ff",
                    }}
                    width={200}
                    format={(percent) => (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                        }}
                      >
                        <span style={{ fontSize: "24px", fontWeight: "bold" }}>
                          {percent}%
                        </span>
                        <span style={{ fontSize: "14px", color: "#8c8c8c" }}>
                          Open Rate
                        </span>
                      </div>
                    )}
                  />

                  <Row
                    gutter={[24, 0]}
                    style={{
                      width: "100%",
                      marginTop: "24px",
                      textAlign: "center",
                    }}
                  >
                    <Col span={12}>
                      <div>
                        <Text type="secondary">Delivered</Text>
                        <div style={{ fontSize: "24px", fontWeight: "bold" }}>
                          {(stats?.overview?.totalDelivered || 0).toLocaleString()}
                        </div>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div>
                        <Text type="secondary">Opened</Text>
                        <div style={{ fontSize: "24px", fontWeight: "bold" }}>
                          {(stats?.overview?.totalOpened || 0).toLocaleString()}
                        </div>
                      </div>
                    </Col>
                  </Row>

                  <div style={{ marginTop: "24px", textAlign: "center" }}>
                    <Text type="secondary" style={{ fontSize: "16px" }}>
                      Industry Average: 21.5%
                    </Text>
                    <br />
                    <Badge
                      status={Number(stats?.overview?.openRate || 0) > 21.5 ? "success" : "warning"}
                      text={
                        <Text>
                          {Number(stats?.overview?.openRate || 0) > 21.5
                            ? "Above Average"
                            : "Below Average"}
                        </Text>
                      }
                    />
                  </div>
                </div>
              </StyledCard>
            </Col>
          </Row>

          {/* Reports Table */}
          <Row gutter={[24, 24]}>
            <Col xs={24}>
              <StyledCard
                title={
                  <Space>
                    <FileTextOutlined />
                    <span>All Pulse Reports</span>
                  </Space>
                }
                extra={
                  <Button
                    type="primary"
                    onClick={() => history.push("/super-admin/pulse-reports/create")}
                  >
                    Create Pulse Report Campaign
                  </Button>
                }
              >
                <Table
                  columns={reportColumns}
                  dataSource={reports}
                  loading={reportsLoading}
                  rowKey="_id"
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} reports`,
                  }}
                />
              </StyledCard>
            </Col>
          </Row>
        </motion.div>
      )}
    </div>
  );
};

export default PulseReportsAnalyticsDashboard;
